import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Card,
  Select,
  MenuItem,
  FormControl,
  Skeleton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  ArrowBack,
  TrendingUp,
  TrendingDown,
  People,
  Star,
  Refresh,
  EmojiEvents,
  Timeline
} from '@mui/icons-material';
import axios from 'axios';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

interface OverviewData {
  totalUsers: number;
  newUsers: number;
  activeUsers: number;
  premiumUsers: number;
  totalMatches: number;
  growthRate: number;
  premiumConversionRate: number;
}

interface GrowthDataPoint {
  date: string;
  newUsers: number;
  totalUsers: number;
}

interface ConversionStep {
  step: string;
  users: number;
  rate: number | string;
}

interface EventData {
  eventName: string;
  count: number;
  value: number;
  description: string;
}

interface CohortData {
  month: string;
  size: number;
  activeUsers: number;
  retention: number;
}

interface MetaAdsData {
  totalSpend: number;
  impressions: number;
  clicks: number;
  reach: number;
  conversions: number;
  appInstalls: number;
  cpi: number;
  roas: number;
  ctr: number;
  campaigns: MetaCampaign[];
  error?: string;
  errorDetails?: string;
}

interface MetaCampaign {
  id: string;
  name: string;
  status: string;
  objective: string;
  spend: number;
  impressions: number;
  clicks: number;
  conversions: number;
  roas: number;
}

const MarketingDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<OverviewData | null>(null);
  const [growthData, setGrowthData] = useState<GrowthDataPoint[]>([]);
  const [conversionData, setConversionData] = useState<ConversionStep[]>([]);
  const [eventsData, setEventsData] = useState<EventData[]>([]);
  const [cohortsData, setCohortsData] = useState<CohortData[]>([]);
  const [metaAdsData, setMetaAdsData] = useState<MetaAdsData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');

      const [overviewRes, growthRes, conversionRes, eventsRes, cohortsRes, metaAdsRes] = await Promise.all([
        axios.get(`https://api.sobcof.com/api/analytics/overview?period=${period}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(`https://api.sobcof.com/api/analytics/growth?period=${period}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(`https://api.sobcof.com/api/analytics/conversion?period=${period}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(`https://api.sobcof.com/api/analytics/events?period=${period}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(`https://api.sobcof.com/api/analytics/cohorts`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(`https://api.sobcof.com/api/analytics/meta-ads?period=${period}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (overviewRes.data.success) setOverview(overviewRes.data.data);
      if (growthRes.data.success) setGrowthData(growthRes.data.data);
      if (conversionRes.data.success) setConversionData(conversionRes.data.data);
      if (eventsRes.data.success) setEventsData(eventsRes.data.data);
      if (cohortsRes.data.success) setCohortsData(cohortsRes.data.data);
      if (metaAdsRes.data.success) {
        console.log('📱 Meta Ads Data:', metaAdsRes.data.data);
        setMetaAdsData(metaAdsRes.data.data);
      }

    } catch (error: any) {
      console.error('Error fetching marketing data:', error);
      setError(error.response?.data?.message || 'Veri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('tr-TR').format(num);
  };

  const formatCurrency = (num: number) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(num);
  };

  const COLORS = {
    primary: '#8B4513',
    secondary: '#D2691E',
    tertiary: '#CD853F',
    success: '#4caf50',
    warning: '#ff9800',
    error: '#f44336',
    gradient1: 'rgba(139, 69, 19, 0.8)',
    gradient2: 'rgba(210, 105, 30, 0.6)'
  };

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Enhanced App Bar */}
      <AppBar
        position="static"
        sx={{
          background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)',
          boxShadow: '0 4px 20px rgba(139, 69, 19, 0.3)'
        }}
      >
        <Toolbar sx={{ minHeight: 70 }}>
          <IconButton color="inherit" onClick={() => navigate('/admin/dashboard')} size="large">
            <ArrowBack />
          </IconButton>
          <Avatar sx={{
            bgcolor: 'rgba(255,255,255,0.2)',
            mr: 2,
            width: 40,
            height: 40
          }}>
            <Timeline sx={{ fontSize: 20 }} />
          </Avatar>
          <Typography variant="h5" component="div" sx={{
            flexGrow: 1,
            fontWeight: 600,
            color: 'white'
          }}>
            Marketing & Growth Analytics
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '& .MuiSelect-icon': { color: 'white' }
                }}
              >
                <MenuItem value="7d">Son 7 Gün</MenuItem>
                <MenuItem value="30d">Son 30 Gün</MenuItem>
                <MenuItem value="90d">Son 90 Gün</MenuItem>
                <MenuItem value="1y">Son 1 Yıl</MenuItem>
              </Select>
            </FormControl>
            <IconButton
              onClick={fetchData}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
              }}
            >
              <Refresh />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3 }}>
        {/* Error Alert */}
        {error && (
          <Box sx={{ mb: 3 }}>
            <Card elevation={0} sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid #fee2e2',
              bgcolor: '#fef2f2'
            }}>
              <Typography variant="body2" color="error">
                ⚠️ {error}
              </Typography>
            </Card>
          </Box>
        )}

        {/* KPI Cards - Box Layout */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
            📊 Key Performance Indicators
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            {/* Toplam Kullanıcılar */}
            <Box sx={{ flex: '1 1 calc(25% - 18px)', minWidth: '250px' }}>
              <Card elevation={0} sx={{
                p: 3,
                borderRadius: 3,
                border: '1px solid #e2e8f0',
                background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)',
                color: 'white',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '100px',
                  height: '100px',
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '50%',
                  transform: 'translate(30%, -30%)'
                }
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                      Toplam Kullanıcı
                    </Typography>
                    {loading ? (
                      <Skeleton variant="text" width={100} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                    ) : (
                      <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                        {formatNumber(overview?.totalUsers || 0)}
                      </Typography>
                    )}
                  </Box>
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 50, height: 50 }}>
                    <People />
                  </Avatar>
                </Box>
                {!loading && overview && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                    {overview.growthRate >= 0 ? (
                      <TrendingUp fontSize="small" />
                    ) : (
                      <TrendingDown fontSize="small" />
                    )}
                    <Typography variant="body2">
                      {Math.abs(overview.growthRate)}% büyüme
                    </Typography>
                  </Box>
                )}
              </Card>
            </Box>

            {/* Yeni Kullanıcılar */}
            <Box sx={{ flex: '1 1 calc(25% - 18px)', minWidth: '250px' }}>
              <Card elevation={0} sx={{
                p: 3,
                borderRadius: 3,
                border: '1px solid #e2e8f0',
                background: 'linear-gradient(135deg, #D2691E 0%, #CD853F 100%)',
                color: 'white',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                      Yeni Kullanıcı
                    </Typography>
                    {loading ? (
                      <Skeleton variant="text" width={100} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                    ) : (
                      <Typography variant="h3" sx={{ fontWeight: 700 }}>
                        {formatNumber(overview?.newUsers || 0)}
                      </Typography>
                    )}
                  </Box>
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 50, height: 50 }}>
                    <TrendingUp />
                  </Avatar>
                </Box>
              </Card>
            </Box>

            {/* Aktif Kullanıcılar */}
            <Box sx={{ flex: '1 1 calc(25% - 18px)', minWidth: '250px' }}>
              <Card elevation={0} sx={{
                p: 3,
                borderRadius: 3,
                border: '1px solid #e2e8f0',
                background: 'linear-gradient(135deg, #CD853F 0%, #DEB887 100%)',
                color: 'white'
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                      Aktif Kullanıcı (7d)
                    </Typography>
                    {loading ? (
                      <Skeleton variant="text" width={100} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                    ) : (
                      <Typography variant="h3" sx={{ fontWeight: 700 }}>
                        {formatNumber(overview?.activeUsers || 0)}
                      </Typography>
                    )}
                  </Box>
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 50, height: 50 }}>
                    <EmojiEvents />
                  </Avatar>
                </Box>
              </Card>
            </Box>

            {/* Premium Kullanıcılar */}
            <Box sx={{ flex: '1 1 calc(25% - 18px)', minWidth: '250px' }}>
              <Card elevation={0} sx={{
                p: 3,
                borderRadius: 3,
                border: '1px solid #e2e8f0',
                background: 'linear-gradient(135deg, #DEB887 0%, #F4A460 100%)',
                color: 'white'
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                      Premium Kullanıcı
                    </Typography>
                    {loading ? (
                      <Skeleton variant="text" width={100} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                    ) : (
                      <>
                        <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5 }}>
                          {formatNumber(overview?.premiumUsers || 0)}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          %{overview?.premiumConversionRate || 0} dönüşüm
                        </Typography>
                      </>
                    )}
                  </Box>
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 50, height: 50 }}>
                    <Star />
                  </Avatar>
                </Box>
              </Card>
            </Box>
          </Box>
        </Box>

        {/* Growth Chart */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
            📈 Kullanıcı Büyümesi
          </Typography>

          <Card elevation={0} sx={{
            p: 3,
            borderRadius: 3,
            border: '1px solid #e2e8f0'
          }}>
            {loading ? (
              <Skeleton variant="rectangular" height={400} />
            ) : (
              <ResponsiveContainer width="100%" height={400}>
                <AreaChart data={growthData}>
                  <defs>
                    <linearGradient id="colorNew" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.8} />
                      <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS.secondary} stopOpacity={0.8} />
                      <stop offset="95%" stopColor={COLORS.secondary} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="date"
                    stroke="#64748b"
                    tickFormatter={(value) => {
                      const date = new Date(value);
                      return `${date.getDate()}/${date.getMonth() + 1}`;
                    }}
                  />
                  <YAxis stroke="#64748b" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="newUsers"
                    stroke={COLORS.primary}
                    fillOpacity={1}
                    fill="url(#colorNew)"
                    name="Yeni Kullanıcılar"
                  />
                  <Area
                    type="monotone"
                    dataKey="totalUsers"
                    stroke={COLORS.secondary}
                    fillOpacity={1}
                    fill="url(#colorTotal)"
                    name="Toplam Kullanıcılar"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Box>

        {/* Conversion Funnel & Events - Side by Side */}
        <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
          {/* Conversion Funnel */}
          <Box sx={{ flex: '1 1 calc(50% - 12px)', minWidth: '400px', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
              🎯 Premium Dönüşüm Hunisi
            </Typography>

            <Card elevation={0} sx={{
              p: 3,
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              flex: 1
            }}>
              {loading ? (
                <Skeleton variant="rectangular" height={400} />
              ) : (
                <Box>
                  {conversionData.map((step) => (
                    <Box key={step.step} sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {step.step}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: COLORS.primary }}>
                          {formatNumber(step.users)} ({step.rate}%)
                        </Typography>
                      </Box>
                      <Box sx={{
                        width: '100%',
                        height: 40,
                        bgcolor: '#f1f5f9',
                        borderRadius: 2,
                        overflow: 'hidden',
                        position: 'relative'
                      }}>
                        <Box sx={{
                          width: `${step.rate}%`,
                          height: '100%',
                          background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 600,
                          transition: 'width 0.5s ease'
                        }}>
                          {parseFloat(step.rate as string) > 15 && `${step.rate}%`}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Card>
          </Box>

          {/* Top Events */}
          <Box sx={{ flex: '1 1 calc(50% - 12px)', minWidth: '400px', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
              🎉 Facebook SDK Events
            </Typography>

            <Card elevation={0} sx={{
              p: 3,
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              flex: 1
            }}>
              {loading ? (
                <Skeleton variant="rectangular" height={400} />
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Event</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Count</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Value</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {eventsData.map((event) => (
                        <TableRow key={event.eventName}>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {event.eventName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {event.description}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={formatNumber(event.count)}
                              size="small"
                              sx={{
                                bgcolor: '#f1f5f9',
                                fontWeight: 600
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.primary }}>
                              {event.value > 0 ? formatCurrency(event.value) : '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Card>
          </Box>
        </Box>

        {/* Meta Ads Performance - THE STAR! */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                📱 Meta Ads Performance
              </Typography>
              {metaAdsData?.error && (
                <Chip
                  label="API Hatası"
                  size="small"
                  color="error"
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Box>
            {metaAdsData?.error && (
              <Card elevation={0} sx={{
                p: 2.5,
                borderRadius: 2,
                border: '1px solid #fee2e2',
                bgcolor: '#fef2f2'
              }}>
                <Typography variant="body2" color="error" sx={{ fontWeight: 600, mb: 1 }}>
                  Meta Ads API Hatası:
                </Typography>
                <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                  {metaAdsData.error}
                </Typography>
                {metaAdsData.errorDetails && (
                  <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1, fontStyle: 'italic' }}>
                    {metaAdsData.errorDetails}
                  </Typography>
                )}
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #fecaca' }}>
                  💡 Token süresi dolmuş olabilir veya izinler yetersiz olabilir. Business Manager'dan yeni token oluşturun ve gerekli izinleri verin.
                </Typography>
              </Card>
            )}
          </Box>

          {/* Meta Ads KPI Cards */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            {/* Total Spend */}
            <Box sx={{ flex: '1 1 calc(25% - 18px)', minWidth: '200px' }}>
              <Card elevation={0} sx={{
                p: 3,
                borderRadius: 3,
                border: '1px solid #e2e8f0',
                background: 'linear-gradient(135deg, #1877f2 0%, #0a5dd8 100%)',
                color: 'white'
              }}>
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                  Toplam Harcama
                </Typography>
                {loading ? (
                  <Skeleton variant="text" width={100} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                ) : (
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {formatCurrency(metaAdsData?.totalSpend || 0)}
                  </Typography>
                )}
              </Card>
            </Box>

            {/* Impressions */}
            <Box sx={{ flex: '1 1 calc(25% - 18px)', minWidth: '200px' }}>
              <Card elevation={0} sx={{
                p: 3,
                borderRadius: 3,
                border: '1px solid #e2e8f0',
                background: 'linear-gradient(135deg, #0a5dd8 0%, #0542a6 100%)',
                color: 'white'
              }}>
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                  Gösterimler
                </Typography>
                {loading ? (
                  <Skeleton variant="text" width={100} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                ) : (
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {formatNumber(metaAdsData?.impressions || 0)}
                  </Typography>
                )}
              </Card>
            </Box>

            {/* App Installs */}
            <Box sx={{ flex: '1 1 calc(25% - 18px)', minWidth: '200px' }}>
              <Card elevation={0} sx={{
                p: 3,
                borderRadius: 3,
                border: '1px solid #e2e8f0',
                background: 'linear-gradient(135deg, #0542a6 0%, #042f74 100%)',
                color: 'white'
              }}>
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                  App Yüklemeleri
                </Typography>
                {loading ? (
                  <Skeleton variant="text" width={100} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                ) : (
                  <>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {formatNumber(metaAdsData?.appInstalls || 0)}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      CPI: {formatCurrency(metaAdsData?.cpi || 0)}
                    </Typography>
                  </>
                )}
              </Card>
            </Box>

            {/* ROAS */}
            <Box sx={{ flex: '1 1 calc(25% - 18px)', minWidth: '200px' }}>
              <Card elevation={0} sx={{
                p: 3,
                borderRadius: 3,
                border: '1px solid #e2e8f0',
                background: 'linear-gradient(135deg, #042f74 0%, #021942 100%)',
                color: 'white'
              }}>
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                  ROAS (Return on Ad Spend)
                </Typography>
                {loading ? (
                  <Skeleton variant="text" width={100} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                ) : (
                  <>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {metaAdsData?.roas || 0}x
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {metaAdsData?.conversions || 0} dönüşüm
                    </Typography>
                  </>
                )}
              </Card>
            </Box>
          </Box>

          {/* Meta Ads Secondary Metrics */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            <Box sx={{ flex: '1 1 calc(33% - 16px)', minWidth: '200px' }}>
              <Card elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid #e2e8f0' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Tıklamalar
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: COLORS.primary }}>
                  {formatNumber(metaAdsData?.clicks || 0)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  CTR: %{metaAdsData?.ctr || 0}
                </Typography>
              </Card>
            </Box>

            <Box sx={{ flex: '1 1 calc(33% - 16px)', minWidth: '200px' }}>
              <Card elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid #e2e8f0' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Erişim
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: COLORS.secondary }}>
                  {formatNumber(metaAdsData?.reach || 0)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Benzersiz kullanıcılar
                </Typography>
              </Card>
            </Box>

            <Box sx={{ flex: '1 1 calc(33% - 16px)', minWidth: '200px' }}>
              <Card elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid #e2e8f0' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Premium Dönüşümler
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: COLORS.tertiary }}>
                  {formatNumber(metaAdsData?.conversions || 0)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatCurrency((metaAdsData?.conversions || 0) * 9.99)} gelir
                </Typography>
              </Card>
            </Box>
          </Box>

          {/* Campaign Performance Table */}
          {metaAdsData && metaAdsData.campaigns && metaAdsData.campaigns.length > 0 && (
            <Card elevation={0} sx={{
              p: 3,
              borderRadius: 3,
              border: '1px solid #e2e8f0'
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                🎯 Kampanya Performansı
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Kampanya</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Durum</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Harcama</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Gösterim</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Tıklama</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Dönüşüm</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">ROAS</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {metaAdsData.campaigns.map((campaign) => (
                      <TableRow key={campaign.id}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {campaign.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {campaign.objective}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={campaign.status}
                            size="small"
                            color={campaign.status === 'ACTIVE' ? 'success' : 'default'}
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatCurrency(campaign.spend)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{formatNumber(campaign.impressions)}</TableCell>
                        <TableCell align="right">{formatNumber(campaign.clicks)}</TableCell>
                        <TableCell align="right">{formatNumber(campaign.conversions)}</TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: campaign.roas > 1 ? COLORS.success : COLORS.error
                            }}
                          >
                            {campaign.roas}x
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          )}
        </Box>

        {/* Cohort Analysis */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
            🔄 Cohort Retention Analizi
          </Typography>

          <Card elevation={0} sx={{
            p: 3,
            borderRadius: 3,
            border: '1px solid #e2e8f0'
          }}>
            {loading ? (
              <Skeleton variant="rectangular" height={300} />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={cohortsData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" stroke="#64748b" />
                  <YAxis stroke="#64748b" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Bar dataKey="size" fill={COLORS.primary} name="Cohort Boyutu" />
                  <Bar dataKey="activeUsers" fill={COLORS.secondary} name="Aktif Kullanıcılar" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default MarketingDashboard;
